// Overlay de búsqueda animado. Controla apertura/cierre (fade + scale),
// micro-animación del ícono, "búsquedas populares" (desde settings del
// tema), "búsquedas recientes" (localStorage) y el estado de "sin
// resultados". No reimplementa el fetch a la Predictive Search API — eso
// sigue siendo el <predictive-search> nativo de Dawn, anidado adentro.

const RECENT_SEARCHES_KEY = 'ricoseller:recent-searches';
const RECENT_SEARCHES_MAX = 5;

class SearchOverlay extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.search-overlay__trigger');
    this.panel = this.querySelector('.search-overlay');
    this.closeButton = this.querySelector('.search-overlay__close');
    this.backdrop = this.querySelector('.search-overlay__backdrop');
    this.input = this.querySelector('input[type="search"]');
    this.emptyState = this.querySelector('[data-search-empty-state]');
    this.noResults = this.querySelector('[data-search-no-results]');
    this.noResultsTermEl = this.querySelector('[data-no-results-term]');
    this.resultsContainer = this.querySelector('[data-predictive-search]');
    this.recentSection = this.querySelector('[data-recent-searches-section]');
    this.recentList = this.querySelector('[data-recent-searches-list]');
    this.form = this.querySelector('form[role="search"]');

    // El header usa transform para el efecto sticky, y eso convierte a
    // cualquier ancestro con transform en el "contenedor" de los
    // descendientes position:fixed (en vez del viewport). Movemos el panel
    // directo al <body> para que el overlay ocupe realmente toda la
    // pantalla, sin quedar recortado dentro del header.
    document.body.appendChild(this.panel);

    this.trigger.addEventListener('click', () => this.open());
    this.trigger.addEventListener('pointerdown', () => this.animateTriggerPress());
    this.closeButton.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.input.addEventListener('input', () => {
      if (this.noResults) this.noResults.hidden = true;
      this.updateEmptyState();
    });

    if (this.form) {
      this.form.addEventListener('submit', () => {
        const term = this.input.value.trim();
        if (term) this.saveRecentSearch(term);
      });
    }

    if (this.resultsContainer) {
      this.resultsContainer.addEventListener('click', (event) => {
        if (event.target.closest('a')) {
          const term = this.input.value.trim();
          if (term) this.saveRecentSearch(term);
        }
      });

      const observer = new MutationObserver(() => this.checkNoResults());
      observer.observe(this.resultsContainer, { childList: true });
    }

    this.wirePopularPills();
    this.renderRecentSearches();
    this.updateEmptyState();
  }

  open() {
    this.panel.hidden = false;
    // Forzar reflow para que la transición de entrada corra (si seteamos la
    // clase en el mismo tick que sacamos "hidden", el navegador la salta).
    void this.panel.offsetHeight;
    this.panel.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('search-overlay-open');
    this.input.focus();
  }

  close() {
    this.panel.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('search-overlay-open');

    const hidePanel = () => {
      this.panel.hidden = true;
    };
    this.panel.addEventListener('transitionend', hidePanel, { once: true });
    // Red de seguridad por si transitionend no dispara (ej. display:none forzado por otro estilo).
    setTimeout(hidePanel, 350);
  }

  animateTriggerPress() {
    this.trigger.classList.add('is-pressed');
    setTimeout(() => this.trigger.classList.remove('is-pressed'), 150);
  }

  updateEmptyState() {
    const hasQuery = this.input.value.trim().length > 0;
    if (this.emptyState) this.emptyState.hidden = hasQuery;
    if (!hasQuery && this.noResults) this.noResults.hidden = true;
  }

  checkNoResults() {
    if (!this.noResults) return;
    const hasQuery = this.input.value.trim().length > 0;
    if (!hasQuery) return;

    const groupsWrapper = this.resultsContainer.querySelector('#predictive-search-results-groups-wrapper');
    this.noResults.hidden = Boolean(groupsWrapper);
    if (!groupsWrapper && this.noResultsTermEl) {
      this.noResultsTermEl.textContent = this.input.value.trim();
    }
  }

  wirePopularPills() {
    this.querySelectorAll('[data-search-pill]').forEach((pill) => {
      pill.addEventListener('click', () => {
        const term = pill.textContent.trim();
        this.input.value = term;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.saveRecentSearch(term);
        this.input.focus();
      });
    });
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(term) {
    if (!term) return;
    let recent = this.getRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase());
    recent.unshift(term);
    recent = recent.slice(0, RECENT_SEARCHES_MAX);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
    this.renderRecentSearches();
  }

  removeRecentSearch(term) {
    const recent = this.getRecentSearches().filter((t) => t !== term);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentList || !this.recentSection) return;
    const recent = this.getRecentSearches();
    this.recentList.innerHTML = '';

    if (recent.length === 0) {
      this.recentSection.hidden = true;
      return;
    }

    this.recentSection.hidden = false;
    recent.forEach((term) => {
      const li = document.createElement('li');
      li.className = 'search-overlay__recent-item';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-overlay__recent-term';
      button.textContent = term;
      button.addEventListener('click', () => {
        this.input.value = term;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      });

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'search-overlay__recent-remove';
      removeButton.setAttribute('aria-label', 'Quitar de búsquedas recientes');
      removeButton.textContent = '×';
      removeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.removeRecentSearch(term);
      });

      li.appendChild(button);
      li.appendChild(removeButton);
      this.recentList.appendChild(li);
    });
  }
}

customElements.define('search-overlay', SearchOverlay);
